import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AppDialog from "../../components/AppDialog/AppDialog";
import { useTranslation } from "react-i18next";
import { getTextByLang } from "../../utils/formUtils";
import { iriLast } from "../../utils/iriLast";
import { extractIdFromIri } from "../../utils/extractFromIri";
import { useGetDatasetOfferDetails } from "../../hooks/catalogs/useGetDatasetOfferDetails";
import { useGetRelatedDatasetsForOffer } from "../../hooks/catalogs/useGetRelatedDatasetsForOffer";
import { usePutCatalog } from "../../hooks/catalogs/usePutCatalog";
import { formatDate, getTranslationsQuality } from "../../utils/constants";
import QualityView from "../../components/QualityView/QualityView";
import { transformQualityReport } from "../../utils/qualityTransformer";
import { useGetQualityReport } from "../../hooks/catalogs/useGetQualityReport";

import { useGetDistributionsByIds } from "../../hooks/distributions/useGetDistributionsByIds";
import { usePostContractAgreementRequestsByAssetIds } from "../../hooks/contracts/usePostContractAgreementRequestsByAssetIds";
import { useGetStartNegotiation } from "../../hooks/contracts/useGetStartNegotiation";
import { useGetTransfer } from "../../hooks/contracts/useGetTransfer";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { resolveParticipantConnectorOrigin } from "../../utils/auth/adminConnectorRuntime";

import "./OfferingDetails.css";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box className="offerDetails-tabPanel">{children}</Box>;
}

function parseWorkerBody(res) {
  if (typeof res?.body === "string") {
    try {
      return JSON.parse(res.body);
    } catch {
      return res.body;
    }
  }

  return res?.data ?? res ?? null;
}

function getContractAgreementItems(value) {
  const body = parseWorkerBody(value);

  if (Array.isArray(body)) return body;

  return [];
}

function getLatestContractAgreementItem(value) {
  const items = getContractAgreementItems(value).filter(
    (item) => item && typeof item === "object",
  );

  if (!items.length) return null;

  return items.reduce((latest, current) => {
    const latestDate = Number(
      latest?.contractSigningDate ?? latest?.contract_signing_date ?? -Infinity,
    );
    const currentDate = Number(
      current?.contractSigningDate ??
        current?.contract_signing_date ??
        -Infinity,
    );

    return currentDate >= latestDate ? current : latest;
  });
}

function pickContractAgreementId(value) {
  const firstItem = getLatestContractAgreementItem(value);

  if (!firstItem || typeof firstItem !== "object") return null;

  return (
    firstItem["@id"] ||
    firstItem.contractAgreementId ||
    firstItem.contract_agreement_id ||
    firstItem.contractAgreement?.id ||
    firstItem.contractAgreement?.["@id"] ||
    firstItem.contractAgreement?.contractAgreementId ||
    firstItem.id ||
    null
  );
}

export default function OfferDetails({
  open,
  onClose,
  offerId,
  callApiWithWorker,
}) {
  const { t, i18n } = useTranslation();
  const { isAdmin, isConsumer, isProvider, decoded, token } = useAuthRoles();
  const lang = i18n.language || "es";
  const canSeeContractActions = isAdmin || isConsumer;
  const participantId = String(decoded?.participant_id || "").trim();
  const getLocalizedText = (value, fallback = "") =>
    getTextByLang(value, lang) ||
    getTextByLang(value, "es") ||
    getTextByLang(value, "en") ||
    fallback;

  const [tab, setTab] = useState(0);
  const translationsQuality = useMemo(() => getTranslationsQuality(t), [t]);

  const offerQ = useGetDatasetOfferDetails({ callApiWithWorker, offerId });
  const offer = offerQ.data;
  const publisherParticipantId = useMemo(
    () => String(extractIdFromIri(offer?.publisher) || offer?.publisher || "").trim(),
    [offer?.publisher],
  );

  const datasetsQ = useGetRelatedDatasetsForOffer({ callApiWithWorker, offer });
  const datasets = datasetsQ.data || [];

  const initialTitle = useMemo(
    () => getLocalizedText(offer?.title),
    [offer, lang],
  );
  const initialDesc = useMemo(
    () => getLocalizedText(offer?.description),
    [offer, lang],
  );

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [providerOrigin, setProviderOrigin] = useState("");

  useEffect(() => {
    let cancelled = false;

    setProviderOrigin("");

    if (!open || !publisherParticipantId || !token) {
      return () => {
        cancelled = true;
      };
    }

    Promise.resolve(
      resolveParticipantConnectorOrigin(publisherParticipantId, token),
    )
      .then((origin) => {
        if (!cancelled) {
          setProviderOrigin(origin);
        }
      })
      .catch((error) => {
        console.error("Error resolving offering provider origin:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [open, publisherParticipantId, token]);

  useEffect(() => {
    if (open && offer) {
      setTitle(getLocalizedText(offer?.title));
      setDescription(getLocalizedText(offer?.description));
      setTab(0);
    }
  }, [open, offer, lang]);

  const dirty = title !== initialTitle || description !== initialDesc;
  const updateMut = usePutCatalog({ callApiWithWorker, t });
  const canEditOffering = useMemo(() => {
    if (isAdmin) return true;
    if (!isProvider || !participantId) return false;

    const nestedPublishers = Array.isArray(offer?.items)
      ? offer.items.map((item) => item?.metadata?.publisher)
      : [];

    return [offer?.publisher, offer?.assigner, ...nestedPublishers].some((value) => {
      const id = extractIdFromIri(value) || value;
      return String(id || "").trim() === participantId;
    });
  }, [
    isAdmin,
    isProvider,
    participantId,
    offer?.publisher,
    offer?.assigner,
    offer?.items,
  ]);

  const dialogTitle = offer
    ? getLocalizedText(offer.title, iriLast(offer.id))
    : t("common.loading");

  const distributionIris = useMemo(() => {
    const iris = [];
    for (const ds of datasets) {
      if (Array.isArray(ds?.distributions)) {
        iris.push(...ds.distributions);
      }
    }
    return Array.from(new Set(iris));
  }, [datasets]);

  const qualityDataset = useMemo(
    () =>
      datasets.find(
        (ds) =>
          ds?.quality_score !== null &&
          typeof ds?.quality_score !== "undefined",
      ) || null,
    [datasets],
  );
  const hasQualityTab = Boolean(qualityDataset);
  const qualityDatasetId = useMemo(
    () => extractIdFromIri(qualityDataset?.id),
    [qualityDataset],
  );

  const distributionIds = useMemo(
    () => distributionIris.map(extractIdFromIri).filter(Boolean),
    [distributionIris],
  );

  const distributionsEnabled = open && tab === 2 && distributionIds.length > 0;

  const distsQ = useGetDistributionsByIds({
    callApiWithWorker,
    ids: distributionIds,
    enabled: distributionsEnabled,
  });

  const qualityTabIndex = 3;
  const rawTabIndex = hasQualityTab ? 4 : 3;

  const qualityQ = useGetQualityReport({
    callApiWithWorker,
    datasetId: qualityDatasetId,
    enabled:
      open &&
      hasQualityTab &&
      tab === qualityTabIndex &&
      Boolean(qualityDatasetId),
  });

  const qualityData = useMemo(
    () => transformQualityReport(qualityQ.data),
    [qualityQ.data],
  );

  const distToDatasetName = useMemo(() => {
    const map = new Map();

    for (const ds of datasets) {
      const nameFromTitle =
        getLocalizedText(ds?.title) ||
        iriLast(ds?.id) ||
        t("dataOfferingsDetail.detail.datasetFallback");

      const distIris = Array.isArray(ds?.distributions) ? ds.distributions : [];
      for (const iri of distIris) {
        const distId = extractIdFromIri(iri);
        if (distId && !map.has(distId)) {
          map.set(distId, nameFromTitle);
        }
      }
    }

    return map;
  }, [datasets, lang]);

  const contractsByAssetQ = usePostContractAgreementRequestsByAssetIds({
    callApiWithWorker,
    assetIds: distributionIds,
    enabled: distributionsEnabled,
  });

  const startNegotiationMut = useGetStartNegotiation({
    t,
    callApiWithWorker,
  });

  const transferMut = useGetTransfer({
    t,
    callApiWithWorker,
  });

  const extFromDistribution = (dist) => {
    const fmt = dist?.format;
    if (!fmt) return "bin";
    return iriLast(fmt).toLowerCase();
  };

  const getProviderOrigin = async () => {
    if (providerOrigin) {
      return providerOrigin;
    }

    if (!publisherParticipantId) {
      throw new Error("Missing offer publisher participant id");
    }

    return resolveParticipantConnectorOrigin(publisherParticipantId, token);
  };

  const handleDistributionAction = async (dist) => {
    const distId = extractIdFromIri(dist?.id);
    if (!distId) {
      console.warn("No distribution id found for distribution:", dist?.id);
      return;
    }

    const datasetName = distToDatasetName.get(distId);
    if (!datasetName) {
      console.warn("No datasetName found for distribution:", dist?.id);
      return;
    }

    const ext = extFromDistribution(dist);
    const contractItems = getContractAgreementItems(
      contractsByAssetQ.data?.[distId],
    );
    const hasExistingContract = contractItems.length > 0;
    const contractAgreementId = pickContractAgreementId(
      contractsByAssetQ.data?.[distId],
    );

    try {
      const resolvedUrlProvider = await getProviderOrigin();

      if (hasExistingContract) {
        await transferMut.mutateAsync({
          identifier: distId,
          datasetName,
          contractAgreementId,
          filename: `distribution-${distId}.${ext}`,
          urlProvider: resolvedUrlProvider,
        });
      } else {
        await startNegotiationMut.mutateAsync({
          identifier: distId,
          datasetName,
          urlProvider: resolvedUrlProvider,
        });
        await contractsByAssetQ.refetch?.();
      }
    } catch {}
  };

  const actions = (
    <>
      <Button onClick={onClose} variant="outlined">
        {t("common.close")}
      </Button>

      {canEditOffering && (
        <Button
          variant="contained"
          disabled={!dirty || updateMut.isPending || !offer}
          onClick={() =>
            updateMut.mutate({
              offerId,
              offer,
              lang,
              title: title.trim(),
              description: description.trim(),
            })
          }
        >
          {updateMut.isPending ? t("common.saving") : t("common.save")}
        </Button>
      )}
    </>
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      subtitle={null}
      maxWidth="lg"
      actions={actions}
      disableBackdropClose={updateMut.isPending}
      disableEscapeClose={updateMut.isPending}
    >
      {offerQ.isLoading ? (
        <div className="offerDetails-loadingRow">
          <CircularProgress size={20} />
          <Typography>{t("common.loading")}</Typography>
        </div>
      ) : offerQ.isError ? (
        <Typography color="error">
          {offerQ.error?.message ?? t("dataOfferingsDetail.detail.offerLoadError")}
        </Typography>
      ) : (
        <div className="offerDetails-grid2">
          {canEditOffering && (
            <>
              <TextField
                label={t("wizard.steps.offer_name")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
              />

              <div className="offerDetails-rightAlignRow" />

              <TextField
                label={t("wizard.steps.offer_description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                className="offerDetails-gridFull"
              />

              <Divider className="offerDetails-gridFull" />
            </>
          )}

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            className="offerDetails-tabsWrap"
          >
            <Tab label={t("wizard.steps.data")} />
            <Tab
              label={`${t("wizard.steps.related")} (${
                offer?.related_resource?.length ?? 0
              })`}
            />
            <Tab
              label={`${t("wizard.steps.dataset.distribution.title")} (${
                distributionIds.length
              })`}
            />
            {hasQualityTab && (
              <Tab label={t("dataOfferingsDetail.detail.quality")} />
            )}
            <Tab label={t("wizard.steps.raw")} />
          </Tabs>

          <div className="offerDetails-tabContent">
            {/* Overview */}
            <TabPanel value={tab} index={0}>
              <div className="offerDetails-overviewGrid">
                <Typography variant="body2">
                  <b>{t("dataOfferings.created")}</b>
                  {formatDate(offer?.created_at)}
                </Typography>
                <Typography variant="body2">
                  <b>{t("dataOfferings.updated")}</b>
                  {formatDate(offer?.updated_at)}
                </Typography>
                <Typography variant="body2">
                  <b>{t("dataOfferings.iri")}</b> {offer?.id}
                </Typography>
              </div>
            </TabPanel>

            {/* Related datasets */}
            <TabPanel value={tab} index={1}>
              {datasetsQ.isLoading ? (
                <div className="offerDetails-loadingRow">
                  <CircularProgress size={20} />
                  <Typography>{t("common.loading")}</Typography>
                </div>
              ) : datasetsQ.isError ? (
                <Typography color="error">
                  {datasetsQ.error?.message ??
                    t("dataOfferingsDetail.detail.relatedDatasetsLoadError")}
                </Typography>
              ) : datasets.length === 0 ? (
                <Typography variant="body2">
                  {t("dataOfferingsDetail.detail.noRelatedDatasets")}
                </Typography>
              ) : (
                <div className="offerDetails-datasetsGrid">
                  {datasets.map((ds) => (
                    <Accordion key={ds?.id || JSON.stringify(ds)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className="offerDetails-accordionTitle">
                          <Typography variant="subtitle1">
                            {getTextByLang(ds?.title, "es") || iriLast(ds?.id)}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="offerDetails-accordionIri"
                          >
                            {ds?.id}
                          </Typography>
                        </div>
                      </AccordionSummary>

                      <AccordionDetails>
                        <div className="offerDetails-accordionBody">
                          <Typography variant="body2">
                            <b>{t("dataOfferingsDetail.detail.descriptionLabel")}</b>
                            {getLocalizedText(ds?.description, "-")}
                          </Typography>

                          {Array.isArray(ds?.distributions) &&
                            ds.distributions.length > 0 && (
                              <div className="offerDetails-chipRow">
                                {ds.distributions.slice(0, 8).map((iri) => (
                                  <Chip
                                    key={iri}
                                    label={`${t("dataOfferingsDetail.detail.distributionLabel")}: ${iriLast(iri)}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                                {ds.distributions.length > 8 && (
                                  <Chip
                                    label={`+${ds.distributions.length - 8}`}
                                    size="small"
                                  />
                                )}
                              </div>
                            )}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </div>
              )}
            </TabPanel>

            {/* Distributions */}
            <TabPanel value={tab} index={2}>
              {distributionIds.length === 0 ? (
                <Typography variant="body2">
                  {t("dataOfferingsDetail.detail.noDistributions")}
                </Typography>
              ) : distsQ.isLoading ? (
                <div className="offerDetails-loadingRow">
                  <CircularProgress size={20} />
                  <Typography>{t("common.loading")}</Typography>
                </div>
              ) : distsQ.isError ? (
                <Typography color="error">
                  {distsQ.error?.message ??
                    t("dataOfferingsDetail.detail.distributionsLoadError")}
                </Typography>
              ) : contractsByAssetQ.isLoading ? (
                <div className="offerDetails-loadingRow">
                  <CircularProgress size={20} />
                  <Typography>{t("common.loading")}</Typography>
                </div>
              ) : contractsByAssetQ.isError ? (
                <Typography color="error">
                  {contractsByAssetQ.error?.message ??
                    t("dataOfferingsDetail.detail.contractAgreementsLoadError")}
                </Typography>
              ) : (
                <div className="offerDetails-distributionList">
                  {(distsQ.data || []).map((dist) => {
                    const distId = extractIdFromIri(dist?.id);
                    const distTitle =
                      getLocalizedText(dist?.title) ||
                      dist?.identifier ||
                      distId;

                    const contractItems = getContractAgreementItems(
                      contractsByAssetQ.data?.[distId],
                    );
                    const hasExistingContract = contractItems.length > 0;
                    const actionLabel = hasExistingContract
                      ? t("common.download")
                      : t("wizard.steps.negotiate");
                    const actionButtonSx = hasExistingContract
                      ? {
                          backgroundColor: "var(--main-color)",
                          color: "#FFFFFF",
                          border: "1px solid var(--main-color)",
                          "&:hover": {
                            backgroundColor: "var(--secondary-color)",
                            borderColor: "var(--secondary-color)",
                            boxShadow: "0 2px 4px #91A3FF",
                          },
                        }
                      : {
                          backgroundColor: "#FFFFFF",
                          color: "var(--main-color)",
                          border: "1px solid var(--main-color)",
                          "&:hover": {
                            backgroundColor: "var(--secondary-color)",
                            borderColor: "var(--secondary-color)",
                            color: "#FFFFFF",
                          },
                        };

                    const actingThis =
                      Boolean(distId) &&
                      ((startNegotiationMut.isPending &&
                        startNegotiationMut.variables?.identifier === distId) ||
                        (transferMut.isPending &&
                          transferMut.variables?.identifier === distId));

                    return (
                      <div
                        key={dist?.id || distId}
                        className="offerDetails-distributionCard"
                      >
                        <div className="offerDetails-distributionsHeader">
                          <div>
                            <Typography variant="subtitle1">
                              {distTitle}
                            </Typography>
                            <Typography
                              variant="caption"
                              className="offerDetails-accordionIri"
                            >
                              {dist?.id || distId}
                            </Typography>
                          </div>

                          {canSeeContractActions && (
                            <Button
                              variant="contained"
                              size="small"
                              sx={actionButtonSx}
                              disabled={!distId || actingThis}
                              onClick={() => handleDistributionAction(dist)}
                            >
                              {actingThis
                                ? t("common.loading")
                                : actionLabel}
                            </Button>
                          )}
                        </div>

                        <div className="offerDetails-distributionMeta">
                          {dist?.download_url && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t("dataOfferingsDetail.detail.downloadUrl")}
                            />
                          )}
                          {dist?.format && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${t("wizard.steps.dataset.distribution.format")}: ${iriLast(dist.format)}`}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabPanel>

            {/* Quality */}
            {hasQualityTab && (
              <TabPanel value={tab} index={qualityTabIndex}>
                {!qualityDatasetId ? (
                  <Typography variant="body2">
                    {t("dataOfferingsDetail.detail.qualityUnavailable") ||
                      "Quality not available."}
                  </Typography>
                ) : qualityQ.isLoading || qualityQ.isFetching ? (
                  <div className="offerDetails-loadingRow">
                    <CircularProgress size={20} />
                    <Typography>
                      {t("common.loading")}
                    </Typography>
                  </div>
                ) : qualityQ.isError ? (
                  <Typography color="error">
                    {t("dataOfferingsDetail.detail.qualityReportLoadError")}
                  </Typography>
                ) : qualityData ? (
                  <QualityView
                    quality={qualityData}
                    translations={translationsQuality}
                  />
                ) : (
                  <Typography variant="body2">
                    {t("dataOfferingsDetail.detail.qualityUnavailable")}
                  </Typography>
                )}
              </TabPanel>
            )}

            {/* Raw */}
            <TabPanel value={tab} index={rawTabIndex}>
              <pre className="offerDetails-rawPre">
                {JSON.stringify({ offer, relatedDatasets: datasets }, null, 2)}
              </pre>
            </TabPanel>
          </div>
        </div>
      )}
    </AppDialog>
  );
}
